<template>
  <div v-if="open" class="validate-cvv-wrapper">
    <div class="validate-cvv">
      <div class="validate-cvv-title">
        <p class="title">{{ $t('message.checkout.validateCVVTitle') }}</p>
        <p class="title-desc">{{ $t('message.checkout.validateCVVDesc') }}</p>
      </div>
      <div class="validate-cvv-content">
        <el-form ref="validateCVVForm" :model="validateForm" :rules="formRules" :hide-required-asterisk="true">
          <el-form-item :label="$t('message.checkout.cardNumber')">
            <el-input disabled v-model="form.cardNum"></el-input>
          </el-form-item>
          <el-form-item :label="$t('message.checkout.cvv')" prop="cvv">
            <el-input class="cvv" v-model="validateForm.cvv" maxlength="4" autofocus @input="onCVVChange">
              <template slot="suffix">
                <div class="img-cvv-box">
                  <el-popover placement="bottom-end" trigger="hover" :visible-arrow="false" :offset="5"
                    popper-class="cvv-popover">
                    <img slot="reference" class="img-cvv" src="@/assets/images/icon-cvv.png" />
                    <div>
                      <div class="cvv-content-top">
                        <img src="@/assets/images/icon-cvv1.png" />
                        <img src="@/assets/images/icon-cvv2.png" />
                      </div>
                      <div class="cvv-content-bottom">{{ $t('message.checkout.cvvTips') }}</div>
                    </div>
                  </el-popover>
                </div>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item v-if="shouldShowIdentityNumber" :label="identityNumberFormLabel" prop="identityNumber">
            <el-input v-model="validateForm.identityNumber" maxlength="64" autocomplete="off" @input="onIdentityNumberInput"></el-input>
          </el-form-item>
        </el-form>
        <el-button type="primary" class="btn-done" :disabled="!isFormValid"
          @click="onDone">{{ $t('message.checkout.continue') }}</el-button>
      </div>
      <div class="btn-close" @click="onCancel"><i class="el-icon-close"></i></div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';

export default {
  computed: {
    ...mapGetters(['app']),
    // 表单验证规则
    formRules() {
      return {
        cvv: [
          { pattern: /^[0-9]{3,4}$/, message: this.$t('message.validator.input', { field: this.$t('message.checkout.cvv').toLowerCase() }), trigger: 'blur' },
        ],
        identityNumber: this.identityNumberRules || [],
      };
    },
    // 表单验证状态
    isFormValid() {
      // CVV校验：有值、长度>=3且为纯数字
      const cvvValid = this.validateForm.cvv &&
        this.validateForm.cvv.length >= 3 &&
        /^[0-9]{3,4}$/.test(this.validateForm.cvv);

      // identityNumber校验
      let identityNumberValid = true;
      if (this.shouldShowIdentityNumber) {
        const configFields = this.form.googlePayPaymentConfigurationFields || [];
        const identityField = configFields.find(field => field.name === 'identityNumber');

        // 如果字段配置为必填，才检查值是否存在
        if (identityField && identityField.require === 'Y') {
          const identityValue = this.validateForm.identityNumber && this.validateForm.identityNumber.trim();
          if (!identityValue) {
            identityNumberValid = false;
          } else {
            // 检查巴西身份证格式
            const country = this.form.country || 'BR';
            if (country === 'BR') {
              const pattern = /^(\d{11}|\d{14}|\d{3}\.\d{3}\.\d{3}-\d{2}|\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2})$/;
              identityNumberValid = pattern.test(identityValue);
            }
          }
        } else {
          // 非必填字段，如果有值才校验格式
          const identityValue = this.validateForm.identityNumber && this.validateForm.identityNumber.trim();
          if (identityValue) {
            const country = this.form.country || 'BR';
            if (country === 'BR') {
              const pattern = /^(\d{11}|\d{14}|\d{3}\.\d{3}\.\d{3}-\d{2}|\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2})$/;
              identityNumberValid = pattern.test(identityValue);
            }
          }
        }
      }

      return cvvValid && identityNumberValid;
    },
    // 判断是否显示identityNumber字段
    shouldShowIdentityNumber() {
      const configFields = this.form.googlePayPaymentConfigurationFields || [];
      return configFields.some(field => field.name === 'identityNumber');
    },
    // 获取identityNumber的校验规则
    identityNumberRules() {
      const configFields = this.form.googlePayPaymentConfigurationFields || [];
      const identityField = configFields.find(field => field.name === 'identityNumber');

      if (!identityField) return [];

      const rules = [];

      // 格式校验（一直存在）
      const country = this.form.country || 'BR';
      if (country === 'BR') {
        rules.push({
          pattern: /^(\d{11}|\d{14}|\d{3}\.\d{3}\.\d{3}-\d{2}|\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2})$/,
          message: this.$t('message.validator.input', { field: this.identityNumberFormLabel.toLowerCase() }),
          trigger: 'blur',
        });
      }

      return rules;
    },
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    identityNumberFormLabel: {
      type: String,
      default: '',
    },
    form: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      validateForm: {
        cvv: '',
        identityNumber: '',
      },
    };
  },
  watch: {
    // 监听form变化，同步identityNumber初始值和默认值
    form: {
      handler(newForm) {
        if (newForm.identityNumber) {
          this.validateForm.identityNumber = newForm.identityNumber;
        }

        // 处理默认值回显
        const configFields = newForm.googlePayPaymentConfigurationFields || [];
        const identityField = configFields.find(field => field.name === 'identityNumber');
        if (identityField && identityField.value && !this.validateForm.identityNumber) {
          this.validateForm.identityNumber = identityField.value;
          // 如果有默认值，执行格式化
          this.$nextTick(() => {
            this.onIdentityNumberInput();
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * 处理CVV输入变化
     * @param {string} value - 输入的值
     * @author Nemo
     * @date 2025-01-14
     */
    onCVVChange(value) {
      this.validateForm.cvv = value.replace(/[^\d]/g, '');
    },
    /**
     * 处理identityNumber输入变化（支持巴西格式化）
     * @author Nemo
     * @date 2025-01-14
     */
    onIdentityNumberInput() {
      const country = this.form.country || 'BR'; // 默认巴西
      const value = this.validateForm.identityNumber;
      if (country === 'BR' && value) {
        let newValue = value.replace(/[^\d]/g, '');
        if (newValue.length === 11) {
          newValue = `${newValue.slice(0, 3)}.${newValue.slice(3, 6)}.${newValue.slice(6, 9)}-${newValue.slice(9, 11)}`;
        } else if (newValue.length === 14) {
          newValue = `${newValue.slice(0, 2)}.${newValue.slice(2, 5)}.${newValue.slice(5, 8)}/${newValue.slice(8, 12)}-${newValue.slice(12, 14)}`;
        }
        this.validateForm.identityNumber = newValue;
      }
    },
    /**
     * 取消操作
     * @author Nemo
     * @date 2025-01-14
     */
    onCancel() {
      this.$emit('cancel');
    },
    /**
     * 完成操作
     * @author Nemo
     * @date 2025-01-14
     */
    onDone() {
      this.$refs.validateCVVForm.validate((valid) => {
        if (valid) {
          this.$emit('done', {
            ...this.form,
            cvv: this.validateForm.cvv,
            identityNumber: this.validateForm.identityNumber,
          });
        }
      });
    },
  },
};
</script>
<style lang="scss">
@import '@/styles/element-variables.scss';

.validate-cvv-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;

  .validate-cvv {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    background-color: white;
    padding-top: 47px;
    padding-left: 33px;
    padding-right: 34px;
    padding-bottom: 48px;
    border-radius: 10px;
    width: 517px;
    box-sizing: border-box;

    .validate-cvv-title {
      width: 100%;

      .title {
        font-weight: 600;
        font-size: 28px;
        color: $color-text-primary;
        line-height: 32px;
        margin: 0;
        text-align: center;
        margin-bottom: 17px;
      }

      .title-desc {
        text-align: left;
        font-weight: 400;
        font-size: 14px;
        color: $color-text-primary;
        line-height: 18px;
        margin: 0;
        margin-bottom: 24px;
      }
    }

    .validate-cvv-content {
      text-align: left;
      width: 100%;

      .el-form .el-form-item {
        margin-bottom: 4px;

        .el-form-item__label {
          font-weight: 600;
          font-size: 14px;
          color: $color-text-primary;
          line-height: 32px;
        }

        .el-input.cvv {
          &.el-input--suffix {
            .el-input__inner {
              padding-right: 60px;
            }
          }
        }

        .el-input.is-disabled .el-input__inner {
          background-color: #F4F4F4;
        }

        .el-input__inner {
          height: 44px;
          line-height: 44px;
        }

        .img-cvv {
          display: block;
          cursor: pointer;
          width: 34px;
          height: 21px;
        }
      }

      .content {
        font-weight: 400;
        font-size: 16px;
        color: $color-text;
        line-height: 20px;
        margin-bottom: 28px;
        text-align: left;
      }

      .btn-done {
        width: 100%;
        border-radius: 2px;
        margin-top: 46px;
      }
    }

    .btn-close {
      position: absolute;
      top: 20px;
      right: 20px;
      width: 20px;
      height: 20px;
      background: #F1F4F8;
      border-radius: 4px;
      color: $color-text;
      cursor: pointer;
    }
  }
}

@media screen and (max-width: 1140px) {
  .validate-cvv-wrapper {
    .validate-cvv {
      width: 344px;
      max-width: 344px;
      // flex-direction: column;
      padding-left: 20px;
      padding-right: 20px;
      padding-top: 42px;
      padding-bottom: 52px;

      .validate-cvv-title {
        .title {
          font-size: 24px;
          margin-bottom: 16px;
        }

        .title-desc {
          margin-bottom: 26px;
        }
      }

      .validate-cvv-content {
        .el-form .el-form-item {
          margin-bottom: 8px;
        }

        .btn-done {
          margin-top: 23px;
        }
      }
    }
  }
}
</style>
